'use client';

import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useMediaQuery } from '../data-table/hooks/use-media-query';

export interface SmartDropdownMenuProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.Root> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SmartDropdownMenu({
  open,
  onOpenChange,
  ...props
}: SmartDropdownMenuProps) {
  // Detect mobile/touch devices
  const isMobile =
    useMediaQuery('(max-width: 768px)') ||
    /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent || '');

  return (
    <DropdownMenuPrimitive.Root
      data-slot="dropdown-menu"
      open={open}
      onOpenChange={onOpenChange}
      modal={isMobile ? false : true}
      {...props}
    />
  );
}

export interface SmartDropdownMenuTriggerProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.Trigger> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SmartDropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  SmartDropdownMenuTriggerProps
>(({ open, onOpenChange, onClick, ...props }, ref) => {
  // Detect mobile/touch devices
  const isMobile =
    useMediaQuery('(max-width: 768px)') ||
    /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent || '');

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // For mobile devices, manually toggle the dropdown
      if (isMobile && onOpenChange) {
        onOpenChange(!open);
      }

      // Call the original onClick if provided
      onClick?.(event);
    },
    [isMobile, onOpenChange, open, onClick],
  );

  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      data-slot="dropdown-menu-trigger"
      onClick={isMobile ? handleClick : onClick}
      {...props}
    />
  );
});

SmartDropdownMenuTrigger.displayName = 'SmartDropdownMenuTrigger';
